import React, { useState, useEffect, SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";

//MUI
import TextField from "@mui/material/TextField";
import Autocomplete, { AutocompleteChangeReason } from "@mui/material/Autocomplete";

//MUI Icons
import LockIcon from "@mui/icons-material/Lock";

//Types
import { IMultiPickerControlProps, PickerItem } from "@/@types/controls/controls";
import { IFieldPickerModel, PickerItemValue } from "@/@types/models/model";

function MultiPickerControl(props: IMultiPickerControlProps) {
  const [inputValue, setInputValue] = useState("");
  const [items, setItems] = useState<Array<PickerItem>>([]);
  const [renderItems, setRenderItems] = useState<Array<PickerItem>>([]);

  const { t } = useTranslation();

  const { field, value, onChange, validation, formMode, controlMode, dc } = props;

  const offset = 100;

  useEffect(() => {
    refreshItems();
  }, []);

  useEffect(() => {
    refreshItems();
  }, [dc]);

  useEffect(() => {
    if (inputValue.length === 0 && renderItems.length === 0 && items.length > 0) {
      getFilteredItems();
    }
  }, [inputValue, items, renderItems]);

  const handleChange = (evt: SyntheticEvent, value: PickerItem[] | null, reason: AutocompleteChangeReason) => {
    switch (reason) {
      case "createOption":
        break;
      case "selectOption":
        applyChange(value);
        break;
      case "removeOption":
        applyChange(value);
        break;
      case "clear":
        applyChange(null);
        break;
      case "blur":
        break;
    }
  };

  const applyChange = (option: PickerItem[] | null) => {
    if (onChange) {
      onChange(option, field.source);
    }
  };

  const getItems = (value: PickerItem[] | PickerItemValue[] | null): PickerItem[] | undefined => {
    if (value === null) {
      //null
      return undefined;
    } else if (Array.isArray(value) && value.length !== 0 && value[0].hasOwnProperty("value") && value[0].hasOwnProperty("label")) {
      //PickerItem
      return value as PickerItem[];
    } else {
      //PickerItemValue
      if (field && field.items && field.items.labels) {
        //items
        var newPickerItems: PickerItem[] = []
        value.forEach((item) => {
          if (field && field.items && field.items.labels) {
            const index = field.items.values.indexOf(item as PickerItemValue) || 0;
            newPickerItems.push({
              label: t(field.items.labels[index]),
              value: field.items.values[index]
            } as PickerItem)
          }
        })
        return newPickerItems
      } else if (field && field.hasOwnProperty("subModel") && dc) {
        //model
        var records: (PickerItem)[] = []
        value.forEach((item) => {
          const record = dc.GetRecord(item as PickerItemValue);
          record ? records.push(record) : {};
        })
        return records
      }
      return undefined;
    }
  };

  const getFilteredItems = (bottom = false) => {
    const limit = bottom ? renderItems.length + offset : offset;
    const retItems = [];

    for (let i = 0; i < limit && i < items.length; ++i) {
      if (inputValue && items[i].label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0) {
        console.log("ptobme")
        retItems.push(items[i]);
      } else if (!inputValue) {
        retItems.push(items[i]);
      }
    }
    setRenderItems(retItems);
  };

  const refreshItems = () => {
    if (field && field.hasOwnProperty("subModel") && dc) {
      dc.GetData().then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const dcItems: Array<PickerItem> = getItems(data)?.filter((x) => x !== null) as PickerItem[];
          setItems(dcItems);
        } else {
          setItems([]);
        }
      });
    } else if (field && field.items && field.items.values) {
      const fieldItems = getItems(field.items.values)
      setItems(fieldItems || []);
    } else {
      setItems([]);
    }
  };

  const isRequired = field && field.validation && field.validation.required ? true : false;


  const loadingText = t("messages.loading");
  const noDataText =
    field && field.hasOwnProperty("subModel") && (field as IFieldPickerModel).external
      ? t("messages.start_typing")
      : t("messages.n_data");

  const hasError = validation && validation.valid === false;
  const isReadOnly = controlMode === "view" || (field && field.readonly);
  const hideLabel = formMode === "table";
  const label = t(field.ttoken);
  const placeholder = field && field.placeholder && field.placeholder.length ? field.placeholder : "";
  let displayValue = "";
  const option: PickerItem[] | undefined = getItems(value)

  return isReadOnly ? (
    <TextField
      margin="none"
      fullWidth
      error={hasError}
      label={hideLabel ? null : label}
      placeholder={placeholder ? t(placeholder) : ""}
      id={field.source}
      value={displayValue}
      InputProps={{
        readOnly: isReadOnly,
        endAdornment: isReadOnly ? <LockIcon style={{ fontSize: 20 }} color="disabled" /> : null
      }}
      helperText={hasError ? t(validation.msg) : field.tooltip ? t(field.tooltip) : ""}
      variant="outlined"
      color="primary"
    />
  ) : (value ? (
    <Autocomplete
      style={{ marginBottom: "20px" }}
      isOptionEqualToValue={(option: PickerItem, value: PickerItem) => option.value === value.value}
      disablePortal
      options={renderItems}
      value={option}
      getOptionLabel={(option: PickerItem) => `${option.label}`}
      renderInput={(params) => <TextField {...params} error={hasError} required={isRequired} label={t(label)} />}
      onChange={handleChange}
      loadingText={loadingText}
      noOptionsText={noDataText}
      multiple={true}
    />) : <></>
  );
}

export default MultiPickerControl;
